
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaBoxOpen,
  FaChartBar,
  FaGraduationCap,
  FaShoppingCart,
  FaStore,
  FaUsers,
} from "react-icons/fa";
import OrdersReports from "../reports/orders-reports/OrdersReports";
import PendingOrderBadge from "../../layout/header/PendingOrderBadge";
import DashboardCharts from "./DashboardCharts";
import { useAdminAccess } from "../../layout/access/AdminAccessContext";

const shortcuts = [
  {
    title: "سفارش‌ها",
    description: "مشاهده و پیگیری سفارش‌ها",
    path: "/order-list",
    icon: FaShoppingCart,
    colors: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  },
  {
    title: "محصولات",
    description: "مدیریت فهرست محصولات",
    path: "/product-list",
    icon: FaBoxOpen,
    colors: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  {
    title: "کاربران",
    description: "دسترسی سریع به کاربران",
    path: "/user-list",
    icon: FaUsers,
    colors: "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  },
  {
    title: "غرفه‌ها",
    description: "بررسی و مدیریت غرفه‌ها",
    path: "/vendor-stores-list",
    icon: FaStore,
    colors: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  },
  {
    title: "دوره‌ها",
    description: "مدیریت دوره‌های آموزشی",
    path: "/course-list",
    icon: FaGraduationCap,
    colors: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  {
    title: "گزارش‌ها",
    description: "آمار و گزارش‌های سفارش‌ها",
    path: "/reports/orders",
    icon: FaChartBar,
    colors: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  },
];

export default function Dashboard() {
  const { canVisit, canReport } = useAdminAccess();
  const visibleShortcuts = shortcuts.filter(item => canVisit(item.path));
  const charts = ["monthly", "topProducts", "fulfillment"].filter(canReport);
  return (
    <div className="space-y-7">
      <section className="relative overflow-hidden rounded-2xl border border-slate-700/40 bg-gradient-to-l from-slate-950 via-slate-900 to-blue-900 px-6 py-6 text-white shadow-lg shadow-slate-900/10 sm:px-8">
        <div aria-hidden="true" className="pointer-events-none absolute -left-8 -top-24 h-48 w-48 rounded-full border-[30px] border-white/5" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-cyan-300/10 blur-2xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="h-10 w-1 rounded-full bg-cyan-300" />
            <div>
              <span className="block text-xs font-medium tracking-wide text-blue-200">پنل مدیریت</span>
              <strong className="mt-1 block text-lg font-semibold">مینا کندل</strong>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {canVisit("/order-list") && <Link
              to="/order-list"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              مشاهده سفارش‌ها
              <FaArrowLeft aria-hidden="true" className="text-xs" />
            </Link>}
            {canVisit("/add-product") && <Link
              to="/add-product"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              افزودن محصول
            </Link>}
          </div>
        </div>
      </section>

      {visibleShortcuts.length > 0 && <section aria-labelledby="dashboard-shortcuts-title">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div className="border-r-[3px] border-blue-600 pr-4 dark:border-blue-400">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">دسترسی سریع</p>
            <h2 id="dashboard-shortcuts-title" className="mt-1 text-xl font-bold text-slate-800 dark:text-slate-100">
              بخش‌های پرکاربرد
            </h2>
          </div>
          <span className="hidden rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 sm:block">{visibleShortcuts.length.toLocaleString("fa-IR")} مسیر سریع</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visibleShortcuts.map(({ title, description, path, icon: Icon, colors }) => (
            <Link
              key={path}
              to={path}
              className="group relative flex min-h-36 flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg hover:shadow-slate-900/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-blue-600"
            >
              <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-blue-600 to-cyan-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
              <div className="flex items-start justify-between gap-3">
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl text-xl ${colors}`}>
                  <Icon aria-hidden="true" />
                </span>
                {path === "/order-list" && (
                  <PendingOrderBadge className="mr-auto min-w-9 rounded-full bg-blue-600 px-2 py-1 text-sm font-bold text-white shadow-md shadow-blue-600/25" />
                )}
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition group-hover:-translate-x-1 group-hover:bg-blue-50 group-hover:text-blue-600 dark:bg-slate-800 dark:group-hover:bg-blue-950 dark:group-hover:text-blue-300">
                  <FaArrowLeft aria-hidden="true" className="text-xs" />
                </span>
              </div>
              <div className="mt-5">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>}

      {charts.length > 0 && <DashboardCharts allowedReports={charts} />}

      {canReport("salesSummary") && <section aria-label="خلاصه فروش" className="pt-1">
        <OrdersReports />
      </section>}
    </div>
  );
}
